
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @module components
import InventoryTMSFilters from '@/modules/TMSModule/components/InventoryTMSFilters/index.vue'
import InventoryTMSRow from '@/modules/TMSModule/components/InventoryTMSRow/index.vue'
import InventoryTMSRowOverall from '@/modules/TMSModule/components/InventoryTMSRowOverall/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import WaitListStatusModal from '@/modules/NewDispatcher/components/WaitListStatusModal/index.vue'

// @types
import { GetInventoryTMSParams, InventoryTMS } from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'

// @composable
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { TakeOrder } from '@/new-services/types/takes'

const TABLE_COLUMNS = [
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'branch',
        name: 'Филиал',
    },
    {
        key: 'navigationName',
        name: 'Лог. направление',
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'orderNumber',
        name: 'Номер заказа',
    },
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'company',
        name: 'Контрагент',
    },
    {
        key: 'transportType',
        name: 'Тип перевозки',
    },
    {
        key: 'hasChangedDeliveryDateConfirmed',
        name: 'Перенос даты доставки',
    },
    {
        key: 'cargoAwaitShipmentDate',
        name: 'Дата размещения',
        filter: 'cargoAwaitShipmentDate',
        filterApplied: false,
    },
    {
        key: 'timeSpentFromPickUp',
        name: 'Часов с момента забора',
    },
    {
        key: 'timerDelivery',
        name: 'Время до прибытия',
    },
    {
        key: 'timer',
        name: 'Контрольный срок до доставки',
    },
    {
        key: 'places',
        name: 'Мест',
    },
    {
        key: 'weight',
        name: 'Физ вес (кг)',
    },
    {
        key: 'cubature',
        name: 'Объем',
    },
    {
        key: 'volumeWeight',
        name: 'Обьемный вес',
    },
    {
        key: 'actions',
        name: 'Действия',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        InventoryTMSFilters,
        'inventory-tms-row': InventoryTMSRow,
        'inventory-tms-row-overall': InventoryTMSRowOverall,
        NewTable,
        NewButton,
        Loader,
        TableScrollWrapper,
        WaitListStatusModal,
    },
    setup() {
        const { saveFile } = useFile()
        const notifications = useNotifications()
        const tablesStore = useTablesStore()

        const wait_list_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
        })

        const state = reactive({
            list: [] as InventoryTMS[],
            totals: {
                totalCubature: 0,
                totalPlaces: 0,
                totalWeight: 0,
                totalVolumeWeight: 0,
            },
            is_excel_loading: false,
            is_loading: false,
            is_failed: false,
            filters: {
                navigationIds: [] as number[],
                warehouseIds: [] as number[],
                transportTypeIds: [] as number[],
                companyId: null as number | null,
                orderBy: 'cargoAwaitShipmentDate',
                orderByDirection: 'asc',
                invoiceNumber: null as string | null,
                orderNumber: null as string | null,
                isIntraCity: null as number | null,
            },
            expanded_rows: [] as number[],
        })

        const setTotals = () => {
            state.list.forEach((item) => {
                if (item.totals) {
                    state.totals.totalCubature = 0
                    state.totals.totalPlaces = 0
                    state.totals.totalWeight = 0
                    state.totals.totalVolumeWeight = 0
                }
            })

            state.list.forEach((item) => {
                if (item.totals) {
                    state.totals.totalCubature += item.totals.totalCubature || 0
                    state.totals.totalPlaces += item.totals.totalPlaces || 0
                    state.totals.totalWeight += item.totals.totalWeight || 0
                    state.totals.totalVolumeWeight += item.totals.totalVolumeWeight || 0
                }
            })
        }

        const loadInventory = async () => {
            try {
                state.is_loading = true

                const params: GetInventoryTMSParams = {
                    ...state.filters,
                }

                const response = await tmsAPI.getInventory(params)
                state.list = response.data.data.map((item, idx) => {
                    item.invoices.forEach((invoice, idx_invoice) => {
                        invoice.index = idx_invoice + 1

                        //@ts-ignore
                        invoice.isYellow = Boolean(invoice.company.isPriority)
                    })

                    return { id: idx + 1, ...item }
                })
                setTotals()

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const manageColumnFilter = () => {
            const column = TABLE_COLUMNS.find((column) => column.key === 'cargoAwaitShipmentDate')
            if (column) {
                column.filterApplied = state.filters.orderByDirection === 'desc'
            }
        }

        const onFiltersChanged = (filters: GetInventoryTMSParams) => {
            state.filters.warehouseIds = filters.warehouseIds
            state.filters.navigationIds = filters.navigationIds
            state.filters.transportTypeIds = filters.transportTypeIds
            state.filters.companyId = filters.companyId
            state.filters.invoiceNumber = filters.invoiceNumber
            state.filters.orderNumber = filters.orderNumber
            state.filters.isIntraCity = filters.isIntraCity

            loadInventory()
        }

        const onFiltersReset = () => {
            state.filters.warehouseIds = []
            state.filters.navigationIds = []
            state.filters.transportTypeIds = []
            state.filters.companyId = null
            state.filters.invoiceNumber = null
            state.filters.orderNumber = null
            state.filters.isIntraCity = null
            state.filters.orderByDirection = 'asc'

            manageColumnFilter()
            loadInventory()
        }

        const downloadReportInventory = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    ...state.filters,
                }

                const response = await tmsAPI.downloadReportInventory(params)

                saveFile(response.data, `Отчет по остаткам`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const manageSortByDate = () => {
            if (state.filters.orderByDirection === 'desc') {
                state.filters.orderByDirection = 'asc'
            } else {
                state.filters.orderByDirection = 'desc'
            }

            manageColumnFilter()
        }

        const sortByDate = async () => {
            try {
                manageSortByDate()
                await loadInventory()
            } catch (error) {
                manageSortByDate()
                console.error(error)
            }
        }

        const isRowExpanded = (id: number) => {
            return state.expanded_rows.includes(id)
        }

        const toggleRow = (item: { id: number }) => {
            const { id } = item

            if (isRowExpanded(id)) {
                const index = state.expanded_rows.findIndex((row_id) => row_id === id)
                if (index >= 0) {
                    state.expanded_rows.splice(index, 1)
                }
            } else {
                state.expanded_rows.push(id)
            }
        }

        const startWaitList = (order: TakeOrder) => {
            wait_list_state.order = { ...order }
            wait_list_state.is_opened = true
        }

        const cancelWaitList = () => {
            wait_list_state.is_opened = false
            wait_list_state.order = null
        }

        const setWaitListStatus = async () => {
            cancelWaitList()
            loadInventory()
        }

        //

        onMounted(loadInventory)

        //

        return {
            state,
            wait_list_state,
            //
            onFiltersChanged,
            onFiltersReset,
            loadInventory,
            downloadReportInventory,
            sortByDate,
            toggleRow,
            tablesStore,
            startWaitList,
            cancelWaitList,
            setWaitListStatus,
            //
            TABLE_COLUMNS,
        }
    },
})
